import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./Dropdown.module.css";

const NOOP = () => {};

export function Dropdown({
  value,
  options,
  showSelectedInMenu,
  placeholder,
  onChange,
  isOpen: isOpenProp,
  onOpenChange,
  renderMenu,
  menuContent,
  buttonClassName,
  buttonOpenClassName,
  valueClassName,
  chevronClassName,
  chevronOpenClassName,
  menuClassName,
  optionClassName,
  selectedOptionClassName,
  menuAriaLabel,
  menuRole,
  buttonBackgroundColor,
  openButtonBackgroundColor,
  openTextColor,
  openBorderColor,
  menuBackgroundColor,
  className,
  children,
}) {
  const [innerIsOpen, setInnerIsOpen] = useState(false);
  const rootRef = useRef(null);
  const isControlled = typeof isOpenProp === "boolean";
  const isOpen = isControlled ? isOpenProp : innerIsOpen;

  const selectedValue = value || "";
  const normalizedSelectedValue = String(selectedValue).trim().toLowerCase();
  const hasCustomMenu =
    typeof renderMenu === "function" || menuContent != null || children != null;

  const visibleOptions = useMemo(() => {
    const uniqueOptions = options.reduce((accumulator, option) => {
      const normalizedOption = String(option || "")
        .trim()
        .toLowerCase();

      if (!normalizedOption) {
        return accumulator;
      }

      if (
        accumulator.some(
          (item) =>
            String(item || "")
              .trim()
              .toLowerCase() === normalizedOption,
        )
      ) {
        return accumulator;
      }

      accumulator.push(option);
      return accumulator;
    }, []);

    if (!showSelectedInMenu) {
      return uniqueOptions.filter(
        (option) =>
          String(option || "")
            .trim()
            .toLowerCase() !== normalizedSelectedValue,
      );
    }

    if (!normalizedSelectedValue) {
      return uniqueOptions;
    }

    const selectedOption = uniqueOptions.find(
      (option) =>
        String(option || "")
          .trim()
          .toLowerCase() === normalizedSelectedValue,
    );

    if (!selectedOption) {
      return uniqueOptions;
    }

    return [
      selectedOption,
      ...uniqueOptions.filter(
        (option) =>
          String(option || "")
            .trim()
            .toLowerCase() !== normalizedSelectedValue,
      ),
    ];
  }, [normalizedSelectedValue, options, showSelectedInMenu]);

  const setIsOpen = (nextValue) => {
    const resolvedValue =
      typeof nextValue === "function" ? nextValue(isOpen) : nextValue;

    if (!isControlled) {
      setInnerIsOpen(resolvedValue);
    }

    onOpenChange?.(resolvedValue);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={classNames(styles.dropdownArea, className)} ref={rootRef}>
      <button
        type="button"
        className={classNames(
          styles.dropdownButton,
          buttonClassName,
          isOpen ? styles.dropdownButtonOpen : null,
          isOpen ? buttonOpenClassName : null,
        )}
        style={{
          "--dropdown-button-bg": buttonBackgroundColor,
          "--dropdown-button-open-bg": openButtonBackgroundColor,
          "--dropdown-open-text": openTextColor,
          "--dropdown-open-border": openBorderColor,
        }}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className={classNames(
            styles.dropdownValue,
            valueClassName,
            selectedValue ? null : styles.placeholder,
          )}
        >
          {selectedValue || placeholder}
        </span>
        <span
          className={classNames(
            styles.chevron,
            chevronClassName,
            isOpen ? styles.chevronUp : null,
            isOpen ? chevronOpenClassName : null,
          )}
        />
      </button>

      {isOpen ? (
        <div
          className={classNames(styles.dropdownMenu, menuClassName)}
          style={{ "--dropdown-menu-bg": menuBackgroundColor }}
          role={menuRole}
          aria-label={menuAriaLabel}
        >
          {hasCustomMenu
            ? renderMenu?.({
                closeMenu: () => setIsOpen(false),
                isOpen,
                visibleOptions,
                selectedValue,
                normalizedSelectedValue,
              }) ||
              menuContent ||
              children
            : visibleOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={classNames(
                    styles.optionButton,
                    optionClassName,
                    String(option || "")
                      .trim()
                      .toLowerCase() === normalizedSelectedValue
                      ? classNames(
                          styles.optionButtonSelected,
                          selectedOptionClassName,
                        )
                      : null,
                  )}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  role="option"
                  aria-selected={
                    String(option || "")
                      .trim()
                      .toLowerCase() === normalizedSelectedValue
                  }
                >
                  {option}
                </button>
              ))}
        </div>
      ) : null}
    </div>
  );
}

Dropdown.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string),
  showSelectedInMenu: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  renderMenu: PropTypes.func,
  menuContent: PropTypes.node,
  buttonClassName: PropTypes.string,
  buttonOpenClassName: PropTypes.string,
  valueClassName: PropTypes.string,
  chevronClassName: PropTypes.string,
  chevronOpenClassName: PropTypes.string,
  menuClassName: PropTypes.string,
  optionClassName: PropTypes.string,
  selectedOptionClassName: PropTypes.string,
  menuAriaLabel: PropTypes.string,
  menuRole: PropTypes.string,
  buttonBackgroundColor: PropTypes.string,
  openButtonBackgroundColor: PropTypes.string,
  openTextColor: PropTypes.string,
  openBorderColor: PropTypes.string,
  menuBackgroundColor: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

Dropdown.defaultProps = {
  value: "",
  options: [],
  showSelectedInMenu: false,
  placeholder: "Select",
  onChange: NOOP,
  isOpen: undefined,
  onOpenChange: undefined,
  renderMenu: undefined,
  menuContent: undefined,
  buttonClassName: undefined,
  buttonOpenClassName: undefined,
  valueClassName: undefined,
  chevronClassName: undefined,
  chevronOpenClassName: undefined,
  menuClassName: undefined,
  optionClassName: undefined,
  selectedOptionClassName: undefined,
  menuAriaLabel: "Dropdown options",
  menuRole: "listbox",
  buttonBackgroundColor: "#ffffff",
  openButtonBackgroundColor: "#283b4d",
  openTextColor: "#91b6bb",
  openBorderColor: "rgba(173, 183, 192, 0.5)",
  menuBackgroundColor: "#243746",
  className: undefined,
  children: undefined,
};

export default Dropdown;
